import { create } from 'zustand';
import { api, AssetPayload, VendorAsset } from '../lib/api';

interface AssetState {
  assets: VendorAsset[];
  total: number;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  selectedCategory: string | null;
  selectedStatus: string | null;
  page: number;
  limit: number;
  assetsById: Record<string, VendorAsset>;

  setSearchQuery: (query: string) => void;
  setCategory: (category: string | null) => void;
  setStatus: (status: string | null) => void;
  reset: () => void;
  fetchAssets: () => Promise<void>;
  getAssetById: (id: number) => Promise<VendorAsset | undefined>;
  createProduct: (payload: AssetPayload) => Promise<VendorAsset>;
  updateProduct: (id: number, payload: Partial<AssetPayload>) => Promise<VendorAsset>;
  deleteProduct: (id: number) => Promise<void>;
  hideAsset: (id: number) => Promise<VendorAsset>;
  unhideAsset: (id: number) => Promise<VendorAsset>;
  submitAsset: (id: number) => Promise<VendorAsset>;
}

export const useAssetStore = create<AssetState>((set, get) => ({
  assets: [],
  total: 0,
  isLoading: false,
  error: null,
  searchQuery: '',
  selectedCategory: null,
  selectedStatus: null,
  page: 1,
  limit: 20,
  assetsById: {},

  setSearchQuery: (query) => set({ searchQuery: query, page: 1 }),
  setCategory: (category) => set({ selectedCategory: category, page: 1 }),
  setStatus: (status) => set({ selectedStatus: status, page: 1 }),
  reset: () =>
    set({
      assets: [],
      total: 0,
      isLoading: false,
      error: null,
      searchQuery: '',
      selectedCategory: null,
      selectedStatus: null,
      page: 1,
    }),

  fetchAssets: async () => {
    const { searchQuery, selectedCategory, selectedStatus, page, limit } = get();
    set({ isLoading: true, error: null });
    try {
      const result = await api.assets.list({
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        status_filter: selectedStatus || undefined,
        limit,
        offset: (page - 1) * limit,
      });
      const assets = result?.assets || [];
      set({
        assets,
        total: result?.total || 0,
        isLoading: false,
        assetsById: assets.reduce(
          (acc: Record<string, VendorAsset>, asset: VendorAsset) => {
            acc[asset.id.toString()] = asset;
            return acc;
          },
          {} as Record<string, VendorAsset>
        ),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch assets';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  getAssetById: async (id: number) => {
    const cached = get().assetsById[id.toString()];
    if (cached) return cached;
    try {
      const asset = await api.assets.get(id);
      set((state) => ({
        assetsById: { ...state.assetsById, [id.toString()]: asset },
      }));
      return asset;
    } catch {
      return undefined;
    }
  },

  createProduct: async (payload: AssetPayload) => {
    const response = await api.assets.create(payload);
    set((state) => ({
      assets: [response, ...state.assets],
      total: state.total + 1,
      assetsById: { ...state.assetsById, [response.id.toString()]: response },
    }));
    return response;
  },

  updateProduct: async (id: number, payload: Partial<AssetPayload>) => {
    const updated = await api.assets.update(id, payload);
    set((state) => ({
      assets: state.assets.map((a) => (a.id === id ? updated : a)),
      assetsById: { ...state.assetsById, [id.toString()]: updated },
    }));
    return updated;
  },

  deleteProduct: async (id: number) => {
    await api.assets.delete(id);
    set((state) => ({
      assets: state.assets.filter((a) => a.id !== id),
      total: Math.max(0, state.total - 1),
      assetsById: Object.fromEntries(
        Object.entries(state.assetsById).filter(([key]) => Number(key) !== id)
      ) as Record<string, VendorAsset>,
    }));
  },

  hideAsset: async (id: number) => {
    const updated = await api.assets.hide(id);
    set((state) => ({
      assets: state.assets.map((a) => (a.id === id ? updated : a)),
      assetsById: { ...state.assetsById, [id.toString()]: updated },
    }));
    return updated;
  },

  unhideAsset: async (id: number) => {
    const updated = await api.assets.unhide(id);
    set((state) => ({
      assets: state.assets.map((a) => (a.id === id ? updated : a)),
      assetsById: { ...state.assetsById, [id.toString()]: updated },
    }));
    return updated;
  },

  submitAsset: async (id: number) => {
    const updated = await api.assets.submit(id);
    set((state) => ({
      assets: state.assets.map((a) => (a.id === id ? updated : a)),
      assetsById: { ...state.assetsById, [id.toString()]: updated },
    }));
    return updated;
  },
}));

export function useAssets() {
  const store = useAssetStore();
  return {
    assets: store.assets,
    total: store.total,
    isLoading: store.isLoading,
    error: store.error,
    searchQuery: store.searchQuery,
    selectedCategory: store.selectedCategory,
    selectedStatus: store.selectedStatus,
    page: store.page,
    limit: store.limit,
    assetsById: store.assetsById,
    setSearchQuery: store.setSearchQuery,
    setCategory: store.setCategory,
    setStatus: store.setStatus,
    reset: store.reset,
    fetchAssets: store.fetchAssets,
    getAssetById: store.getAssetById,
    createProduct: store.createProduct,
    updateProduct: store.updateProduct,
    deleteProduct: store.deleteProduct,
    hideAsset: store.hideAsset,
    unhideAsset: store.unhideAsset,
    submitAsset: store.submitAsset,
  };
}
